import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Loader2, MessageCircle, RefreshCw, Send, AlertTriangle,
  Sparkles, ExternalLink, Briefcase,
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { API } from "../../App";

const MAX_LEN = 16384;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

/* Thumbtack conversation pane for the Messages page.
   Completely separate from the SMS chat pane — its own thread loader and its
   own send path (POST /thumbtack/negotiations/{id}/messages). Nothing here
   can route through Twilio. */
const ThumbtackConversationPane = ({ conv, onBack, onJobClick, onChanged }) => {
  const negId = conv?.negotiation_id;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [meta, setMeta] = useState({});
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [sendError, setSendError] = useState(null);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);

  const load = useCallback(async (soft = false) => {
    if (!negId) return;
    soft ? setRefreshing(true) : setLoading(true);
    try {
      const res = await axios.get(`${API}/thumbtack/negotiations/${negId}/messages`, { headers: headers() });
      setMessages(res.data.messages || []);
      setMeta({ stale: res.data.stale, error: res.data.error, writeEnabled: res.data.messaging_write_enabled });
    } catch (e) {
      const d = e.response?.data?.detail;
      setMeta({ stale: true, error: (typeof d === "object" ? d?.code : null) || "load_failed" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [negId]);

  useEffect(() => {
    setMessages([]); setDraft(""); setSendError(null); setMeta({});
    load();
  }, [load]);

  // Mark inbound messages read as soon as the thread opens.
  useEffect(() => {
    if (!negId) return;
    axios.post(`${API}/thumbtack/conversations/${negId}/read`, {}, { headers: headers() })
      .then(() => onChanged?.()).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negId]);

  // Soft-refresh the thread every 5s while open.
  useEffect(() => {
    if (!negId) return;
    const iv = setInterval(() => load(true), 5000);
    return () => clearInterval(iv);
  }, [negId, load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    if (text.length > MAX_LEN) {
      setSendError({ code: "too_long", message: `Message is too long (${text.length.toLocaleString()} characters) — Thumbtack allows up to 16,384.` });
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const res = await axios.post(`${API}/thumbtack/negotiations/${negId}/messages`, { text }, { headers: headers() });
      setMessages((prev) => [...prev, res.data.message]);
      setDraft(""); // cleared ONLY on success — a failed reply stays for retry
      toast.success("Reply sent on Thumbtack");
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      const err = typeof d === "object" && d ? d : { code: "send_failed", message: typeof d === "string" ? d : "Could not send the message. Try again." };
      setSendError(err);
      toast.error(err.message || "Reply not sent");
    } finally {
      setSending(false);
    }
  };

  const suggest = async () => {
    if (suggesting || sending) return;
    setSuggesting(true);
    try {
      const res = await axios.post(`${API}/thumbtack/negotiations/${negId}/suggest-reply`, {}, { headers: headers() });
      setDraft(res.data.suggestion || "");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not draft a suggestion — try again.");
    } finally {
      setSuggesting(false);
    }
  };

  if (!conv) {
    return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  const needsReconnect = sendError?.code === "missing_scope" || sendError?.code === "reconnect_required" || meta.error === "reconnect_required";
  const overLimit = draft.length > MAX_LEN;

  return (
    <>
      {/* Header */}
      <div className="px-3 py-2 flex items-center gap-3 border-b border-[#009fd9]/25 bg-[#009fd9]/[0.04] flex-shrink-0" data-testid="tt-chat-header">
        <button onClick={onBack} className="md:hidden p-1.5 rounded-lg hover:bg-muted/50" data-testid="tt-chat-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-[#009fd9]/15 text-[#009fd9]">
          <MessageCircle className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{conv.contact_name || "Thumbtack customer"}</p>
            <span className="text-[9px] font-semibold uppercase tracking-wide text-[#009fd9] bg-[#009fd9]/10 border border-[#009fd9]/25 rounded px-1.5 py-px flex-shrink-0" data-testid="tt-channel-badge">Thumbtack</span>
          </div>
          <p className="text-[11px] text-muted-foreground truncate">
            {conv.job_display_id ? `${conv.job_display_id} · ${conv.job_title || "Thumbtack lead"}` : "Thumbtack lead"}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {conv.job_id && (
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1 border-[#009fd9]/40 text-[#009fd9] hover:bg-[#009fd9]/10" onClick={() => onJobClick(conv.job_id)} data-testid="tt-open-job-btn">
              <Briefcase className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Open job</span> <ExternalLink className="w-3 h-3" />
            </Button>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => load(true)} title="Refresh thread" data-testid="tt-thread-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Thread */}
      <div className="relative flex-1 min-h-0">
        <div ref={scrollRef} className="absolute inset-0 overflow-y-auto px-3 py-2 overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }} data-testid="tt-messages-area">
          {loading ? (
            <div className="flex items-center justify-center p-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <MessageCircle className="w-10 h-10 text-[#009fd9]/30 mb-3" />
              <p className="text-sm text-muted-foreground">No messages on this Thumbtack lead yet</p>
            </div>
          ) : (
            messages.map((m) => (
              <div key={m.message_id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"} mb-1`}>
                <div className={`max-w-[80%] sm:max-w-[70%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${m.direction === "outbound" ? "bg-[#009fd9] text-white rounded-br-md" : "bg-muted/60 text-foreground rounded-bl-md"}`} style={{ overflowWrap: "anywhere" }} data-testid={`tt-msg-${m.message_id}`}>
                  {m.text}
                  <p className={`text-[10px] mt-0.5 ${m.direction === "outbound" ? "text-white/70" : "text-muted-foreground"}`}>
                    {m.direction === "outbound" ? "You" : m.sender_name || "Customer"}{fmtTime(m.sent_at) ? ` · ${fmtTime(m.sent_at)}` : ""}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} className="h-1" />
        </div>
      </div>

      {/* Status banners + composer — Thumbtack-only send path */}
      <div className="border-t border-[#009fd9]/25 flex-shrink-0">
        {meta.error === "reconnect_required" && (
          <div className="mx-3 mt-2 rounded-lg bg-amber-500/10 border border-amber-500/20 p-2 flex items-center gap-2" data-testid="tt-reconnect-banner">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <p className="text-[11px] text-foreground">
              Thumbtack needs reconnection — <a href="/dashboard/settings?tab=integrations" className="font-medium underline">reconnect it in Settings → Integrations</a> to refresh and reply.
            </p>
          </div>
        )}
        {meta.stale && meta.error && meta.error !== "reconnect_required" && messages.length > 0 && (
          <p className="mx-3 mt-2 text-[11px] text-muted-foreground" data-testid="tt-thread-stale">
            Showing saved copy — couldn't refresh from Thumbtack right now.
          </p>
        )}
        {sendError && (
          <div className="mx-3 mt-2 rounded-lg bg-red-500/10 border border-red-500/20 p-2 space-y-1" data-testid="tt-send-error">
            <p className="text-[11px] text-red-500 font-medium">Reply not sent: {sendError.message || "Could not send the message."}</p>
            {needsReconnect ? (
              <a href="/dashboard/settings?tab=integrations" className="inline-block text-[11px] font-medium underline text-foreground" data-testid="tt-reconnect-link">
                Reconnect Thumbtack
              </a>
            ) : sendError.code !== "too_long" ? (
              <Button variant="outline" size="sm" className="h-6 text-[11px] px-2" onClick={send} disabled={sending} data-testid="tt-send-retry">
                Retry
              </Button>
            ) : null}
          </div>
        )}
        <div className="flex items-end gap-2 p-3">
          <Button
            variant="outline" size="icon"
            className="h-10 w-10 rounded-xl border-[#009fd9]/40 text-[#009fd9] hover:bg-[#009fd9]/10 flex-shrink-0"
            onClick={suggest} disabled={suggesting || sending || messages.length === 0}
            title="AI: draft a suggested reply" data-testid="tt-suggest-btn"
          >
            {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          </Button>
          <div className="flex-1 min-w-0">
            <Textarea
              value={draft}
              onChange={(e) => { setDraft(e.target.value); if (sendError?.code === "too_long" && e.target.value.length <= MAX_LEN) setSendError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Reply on Thumbtack…" rows={1}
              className={`min-h-[40px] max-h-[120px] resize-none bg-muted/30 text-sm ${overLimit ? "border-red-500/60" : "border-[#009fd9]/30 focus:border-[#009fd9]/60"}`}
              data-testid="tt-reply-input"
            />
            {draft.length > 15000 && (
              <p className={`text-[10px] mt-0.5 ${overLimit ? "text-red-500 font-medium" : "text-muted-foreground"}`} data-testid="tt-char-counter">
                {draft.length.toLocaleString()} / 16,384 characters
              </p>
            )}
          </div>
          <Button
            onClick={send} disabled={!draft.trim() || overLimit || sending} size="icon"
            className="h-10 w-10 rounded-xl flex-shrink-0 bg-[#009fd9] text-white hover:bg-[#008ec2]"
            data-testid="tt-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ThumbtackConversationPane;
